import { FileExtension } from "@nova/constants"
import { FunctionComponent, useState } from "react"
import { ColorFilter } from "./ColorFinderPanel"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { Divider } from "components/Divider"
import { ColorPicker } from "features/map/inspector-panel/icons/TODO-put-in-package/ColorPicker"
import RangeSlider from "./RangeSlider"
import { ImageColorSampler } from "./ImageColorSampler"
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import { ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Popup } from "@nova/component-popup"
import { getColorRangeBounds, getComprehensive15BitRange } from "../ColorRange"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"

export interface ColorSampleFilter {
    from: string
    to: string
    filter: ColorFilter
    baseColor: string
}
interface ColorSampleProps {
    onComplete: (filter: ColorSampleFilter) => void
}

const ColorSample: FunctionComponent<ColorSampleProps> = ({ onComplete }) => {
    const [file, setFile] = useState<File>()

    const handleComplete = (filter: ColorSampleFilter) => {
        setFile(undefined)
        onComplete(filter)
    }
    const dropbox = (
        <UploadDropBox
            maxFileSizeInMb={50}
            supportedMimeTypes={[FileExtension.jpg, FileExtension.jpeg, FileExtension.png].map(
                (type) => mapExtensionsToMimeType[type]
            )}
            title="Upload Image"
            allowedFileTypes={[FileExtension.jpg, FileExtension.jpeg, FileExtension.png]}
            isProcessing={false}
            onNewItems={async (items) => {
                setFile(items.at(0))
            }}
        />
    )

    return (
        <>
            {dropbox}
            {file && (
                <ColorSampleModal
                    onClose={() => {
                        setFile(undefined)
                    }}
                    media={file}
                    onComplete={handleComplete}
                />
            )}
        </>
    )
}

interface ColorSampleModalProps {
    media: File
    onComplete: (filter: ColorSampleFilter) => void
    onClose: () => void
}

type Range = { min: number; max: number }

const ColorSampleModal: FunctionComponent<ColorSampleModalProps> = ({
    media,
    onComplete,
    onClose,
}) => {
    const [selectedColor, setSelectedColor] = useState<string>()
    const [saturationRange, setSaturationRange] = useState<Range>({ min: 40, max: 60 })
    const [brightnessRange, setBrightnessRange] = useState<Range>({ min: 40, max: 60 })
    const [hueRange, setHueRange] = useState<Range>({ min: 30, max: 70 })

    const bounds = selectedColor
        ? getColorRangeBounds(selectedColor, hueRange, saturationRange, brightnessRange)
        : undefined

    return (
        <HighPriorityNovaModalContainer
            title="Select a Color Sample"
            className="text-gray-200"
            closeModal={onClose}
        >
            <div className="flex h-full max-w-4xl w-full max-h-128 gap-2.5">
                <div className="flex-1 relative min-w-0 border border-white-200 rounded-lg overflow-hidden">
                    <TransformWrapper
                        maxScale={100}
                        velocityAnimation={{ disabled: true }}
                        wheel={{ smoothStep: 0.0175 }}
                        doubleClick={{ disabled: true }}
                        panning={{ allowLeftClickPan: false }}
                    >
                        <TransformComponent
                            contentClass="!w-full !h-full"
                            wrapperClass="!w-full !h-full"
                        >
                            <ImageColorSampler file={media} onColorSelect={setSelectedColor} />
                        </TransformComponent>
                    </TransformWrapper>
                </div>
                <div className="flex flex-col basis-64 bg-white-100 p-3 rounded-lg border border-white-200 gap-5">
                    <div className="flex flex-col gap-1">
                        <h5>Base Color</h5>
                        <div className="flex gap-2.5">
                            <Popup
                                content={
                                    <Popup.PopupModal>
                                        <ColorPicker
                                            selectedColor={selectedColor ?? "#000000"}
                                            setSelectedColor={setSelectedColor}
                                            debounceMs={0}
                                        />
                                    </Popup.PopupModal>
                                }
                            >
                                <div
                                    className="aspect-square h-10 rounded-md"
                                    style={{ backgroundColor: selectedColor ?? "#000000" }}
                                />
                            </Popup>
                            <h5 className="bg-gray-700 px-4 py-2 text-white rounded-md w-[90px] h-9">
                                {selectedColor}
                            </h5>
                        </div>
                    </div>
                    <div>
                        <h5>Hue</h5>
                        <div className="flex items-center gap-1">
                            <small className="text-gray-400">Min</small>
                            <RangeSlider
                                value={hueRange}
                                onChange={(range) => setHueRange(range)}
                                trackStyle={{
                                    background: `linear-gradient(to right, 
                                    hsl(from ${selectedColor} calc(h - 50) s l), 
                                    ${selectedColor}, 
                                    hsl(from ${selectedColor} calc(h + 50) s l)
                                )`,
                                }}
                            />
                            <small className="text-gray-400">Max</small>
                        </div>
                    </div>

                    <div>
                        <h5>Saturation</h5>
                        <div className="flex items-center gap-1">
                            <small className="text-gray-400">Min</small>
                            <RangeSlider
                                value={saturationRange}
                                onChange={(range) => setSaturationRange(range)}
                                trackStyle={{
                                    background: `linear-gradient(to right, 
                                    hsl(from ${selectedColor} h 0 l), 
                                    ${selectedColor}, 
                                    hsl(from ${selectedColor} h 100 l)
                                )`,
                                }}
                            />
                            <small className="text-gray-400">Max</small>
                        </div>
                    </div>

                    <div>
                        <h5>Brightness</h5>
                        <div className="flex items-center gap-1">
                            <small className="text-gray-400">Min</small>
                            <RangeSlider
                                value={brightnessRange}
                                onChange={(range) => setBrightnessRange(range)}
                                trackStyle={{
                                    background: `linear-gradient(to right, black, ${selectedColor}, white)`,
                                }}
                            />

                            <small className="text-gray-400">Max</small>
                        </div>
                    </div>
                    <Divider />

                    <div className="flex gap-4 items-center">
                        <small className="text-gray-400">Range</small>
                        <div className="flex items-center gap-2.5">
                            <h5 className="bg-gray-700 px-4 py-2 text-white rounded-md w-[90px] h-9">
                                {bounds?.from}
                            </h5>
                            <h5 className="text-gray-400">-</h5>
                            <h5 className="bg-gray-700 px-4 py-2 text-white rounded-md w-[90px] h-9">
                                {bounds?.to}
                            </h5>
                        </div>
                    </div>

                    <ButtonPrimary
                        title="Search"
                        RightIcon={Icons.ArrowRight}
                        disabled={!selectedColor}
                        onClick={() => {
                            if (!selectedColor || !bounds) return

                            onComplete({
                                filter: getComprehensive15BitRange(
                                    selectedColor,
                                    hueRange,
                                    saturationRange,
                                    brightnessRange
                                ),
                                from: bounds.from,
                                to: bounds.to,
                                baseColor: selectedColor,
                            })
                        }}
                    />
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { ColorSample }
